import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  ShoppingCart,
  ShoppingBag,
  ClipboardList,
  CreditCard,
  Receipt,
  User,
  Shield,
  ChevronDown,
  ChevronRight,
  LogOut,
  Key,
  X,
  UserCircle,
  HelpCircle,
} from "lucide-react";

import { useAuth } from "../context/AuthContext";
import ChangePasswordModal from "./ChangePasswordModal";
import { motion, AnimatePresence } from "framer-motion";

// Menu item component with modern styling
const MenuItem = ({
  item,
  isActive,
  isExpanded,
  onToggle,
  depth = 0,
  currentPath,
  isCollapsed,
}) => {
  const hasChildren = item.items && item.items.length > 0;

  if (hasChildren) {
    return (
      <div className="mb-0.5">
        <button
          onClick={onToggle}
          className={`group w-full flex items-center justify-between px-3 py-2.5 text-[13px] font-medium transition-all duration-200 rounded-xl mx-auto
            ${
              isActive || isExpanded
                ? "bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 dark:from-blue-900/40 dark:to-indigo-900/40 dark:text-blue-300"
                : "text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800/50"
            }
            ${isCollapsed ? "justify-center w-12 mx-auto" : "w-full"}`}
          title={isCollapsed ? item.label : undefined}
        >
          <div className="flex items-center gap-3">
            {item.icon && (
              <item.icon
                className={`w-5 h-5 flex-shrink-0 transition-colors duration-200 ${
                  isActive || isExpanded
                    ? "text-blue-600 dark:text-blue-400"
                    : "text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300"
                }`}
              />
            )}
            {!isCollapsed && <span>{item.label}</span>}
          </div>
          {!isCollapsed && (
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown
                className={`w-4 h-4 transition-colors duration-200 ${
                  isActive || isExpanded ? "text-blue-500" : "text-gray-400"
                }`}
              />
            </motion.div>
          )}
        </button>

        <AnimatePresence initial={false}>
          {isExpanded && !isCollapsed && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="overflow-hidden"
            >
              <div className="mt-1 ml-4 pl-4 border-l-2 border-gray-100 dark:border-gray-700 space-y-0.5">
                {item.items.map((subItem) => (
                  <MenuItem
                    key={subItem.id}
                    item={subItem}
                    isActive={currentPath && currentPath.includes(subItem.path)}
                    depth={depth + 1}
                    currentPath={currentPath}
                    isCollapsed={isCollapsed}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <Link
      to={`/customer${item.path}`}
      className={`group flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium transition-all duration-200 rounded-xl mb-0.5
        ${
          isActive
            ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/25"
            : "text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800/50"
        }
        ${isCollapsed ? "justify-center w-12 mx-auto" : "w-full"}
        ${depth > 0 ? "py-2" : ""}`}
      title={isCollapsed ? item.label : undefined}
    >
      {item.icon && (
        <item.icon
          className={`w-5 h-5 flex-shrink-0 transition-colors duration-200 ${
            isActive
              ? "text-white"
              : "text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300"
          }`}
        />
      )}
      {!isCollapsed && <span>{item.label}</span>}
      {isActive && !isCollapsed && (
        <div className="ml-auto w-1.5 h-1.5 bg-white rounded-full" />
      )}
    </Link>
  );
};

export default function CustomerSidebar({
  isOpen,
  onClose,
  isCollapsed = false,
}) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const handleLogout = () => {
    logout();
  };

  const menuItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      path: "/dashboard",
      icon: LayoutDashboard,
    },
    {
      id: "shopping",
      label: "Shopping",
      icon: ShoppingCart,
      items: [
        {
          id: "products",
          label: "Browse Products",
          path: "/products",
          icon: ShoppingBag,
        },
        {
          id: "cart",
          label: "My Cart",
          path: "/cart",
          icon: ShoppingCart,
        },
      ],
    },
    {
      id: "orders",
      label: "Orders & Fulfillment",
      icon: ClipboardList,
      items: [
        {
          id: "my-orders",
          label: "My Orders",
          path: "/orders",
          icon: ClipboardList,
        },
      ],
    },
    {
      id: "financial",
      label: "Financial",
      icon: CreditCard,
      items: [
        {
          id: "invoices",
          label: "Invoices",
          path: "/invoices",
          icon: Receipt,
        },
        {
          id: "payables",
          label: "Payables",
          path: "/payables",
          icon: CreditCard,
        },
      ],
    },
    {
      id: "account",
      label: "Account",
      icon: User,
      items: [
        {
          id: "profile",
          label: "Profile",
          path: "/profile",
          icon: User,
        },
        {
          id: "change-password",
          label: "Security",
          path: "/change-password",
          icon: Shield,
        },
      ],
    },
  ];

  // Initialize expanded menu based on current path
  const [expandedMenu, setExpandedMenu] = useState(() => {
    const activeItem = menuItems.find(
      (item) =>
        item.items &&
        item.items.some((subItem) => location.pathname.includes(subItem.path)),
    );
    return activeItem ? activeItem.id : null;
  });

  const toggleMenu = (menuId) => {
    setExpandedMenu((prev) => (prev === menuId ? null : menuId));
  };

  const isGroupActive = (item) => {
    if (!item.items) return false;
    return item.items.some((subItem) =>
      location.pathname.includes(subItem.path),
    );
  };

  const isLinkActive = (path) => {
    return location.pathname.includes(path);
  };

  return (
    <>
      {/* Mobile Overlay */}
      <div
        className={`fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-40 lg:hidden transition-all duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Sidebar Container */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex flex-col bg-white dark:bg-gray-900 border-r border-gray-200/80 dark:border-gray-800 transform transition-all duration-300 ease-out
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
          ${isCollapsed ? "w-20" : "w-72"}`}
      >
        {/* Header */}
        <div className="relative flex items-center justify-between h-16 px-4 border-b border-gray-200/80 dark:border-gray-800 bg-gradient-to-r from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
          {!isCollapsed && (
            <>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-900" />
                </div>
                <div>
                  <h1 className="text-sm font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                    Axia B2B
                  </h1>
                  <p className="text-[10px] text-blue-600 dark:text-blue-400 font-semibold tracking-wide">
                    Customer Portal
                  </p>
                </div>
              </div>

              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors lg:hidden"
              >
                <X className="w-5 h-5" />
              </button>
            </>
          )}
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          {menuItems.map((item) => (
            <MenuItem
              key={item.id}
              item={item}
              isActive={
                item.items ? isGroupActive(item) : isLinkActive(item.path)
              }
              isExpanded={expandedMenu === item.id}
              onToggle={() => toggleMenu(item.id)}
              currentPath={location.pathname}
              isCollapsed={isCollapsed}
            />
          ))}
        </nav>

        {/* User Profile Footer */}
        <div className="p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
          <div className="relative">
            <button
              onClick={() =>
                !isCollapsed && setShowProfileMenu(!showProfileMenu)
              }
              className={`w-full flex items-center justify-between rounded-xl transition-all duration-200 ${
                isCollapsed
                  ? "justify-center"
                  : "hover:bg-gray-100 dark:hover:bg-gray-700/50 p-2 -m-2 cursor-pointer"
              }`}
            >
              <div
                className={`flex items-center ${
                  isCollapsed ? "justify-center w-full" : ""
                }`}
              >
                <div className="relative">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-blue-500/25">
                    {user?.name?.charAt(0)?.toUpperCase() || "C"}
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-800" />
                </div>

                {!isCollapsed && (
                  <div className="ml-3 min-w-0 flex-1 text-left">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {user?.name || "Customer"}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {user?.email || "customer@ennovace.com"}
                    </p>
                  </div>
                )}
              </div>

              {!isCollapsed && (
                <ChevronRight
                  className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                    showProfileMenu ? "rotate-90" : ""
                  }`}
                />
              )}
            </button>

            {!isCollapsed && (
              <AnimatePresence>
                {showProfileMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-[60]"
                      onClick={() => setShowProfileMenu(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: 10 }}
                      transition={{ duration: 0.15 }}
                      className="absolute bottom-full right-0 mb-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-[70] overflow-hidden"
                    >
                      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {user?.name || "Customer"}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {user?.email || "customer@ennovace.com"}
                        </p>
                        {user?.businessName && (
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 font-medium">
                            {user.businessName}
                          </p>
                        )}
                      </div>

                      <div className="py-1">
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            setShowChangePasswordModal(true);
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center gap-3 transition-colors"
                        >
                          <Key className="w-4 h-4 text-gray-400" />
                          Change Password
                        </button>
                        <Link
                          to="/customer/profile"
                          onClick={() => setShowProfileMenu(false)}
                          className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center gap-3 transition-colors"
                        >
                          <UserCircle className="w-4 h-4 text-gray-400" />
                          View Profile
                        </Link>
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center gap-3 transition-colors"
                        >
                          <HelpCircle className="w-4 h-4 text-gray-400" />
                          Help Center
                        </button>
                      </div>

                      <div className="border-t border-gray-100 dark:border-gray-700 pt-1 mt-1">
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            handleLogout();
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-3 transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            )}
          </div>
        </div>
      </aside>

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <ChangePasswordModal
          isOpen={showChangePasswordModal}
          onClose={() => setShowChangePasswordModal(false)}
          userType="customer"
        />
      )}
    </>
  );
}
